<template>
    <form class="scope-number-input__form" ref="numberCodeForm">
        <fieldset
            :class="[
                'scope-number-input__fieldset',
                { '-space-between': spaceBetween }
            ]"
            data-number-code-form
        >
            <input
                type="number"
                :disabled="disabled"
                maxlength="1"
                v-for="(el, index) in necessaryLength"
                :key="index"
                :class="['scope-number-input', { '-invalid': isInvalid }]"
                min="0"
                max="9"
                data-number-code-input
                @input="handleInput($event, index)"
                @keydown="handleKeyDown($event, index)"
                required
            />
        </fieldset>
        <div
            class="scope-number-input__error-message"
            v-if="isInvalid && errorMessage"
        >
            {{ errorMessage }}
        </div>
    </form>
</template>

<script setup>
import { ref } from 'vue';
const props = defineProps({
    necessaryLength: {
        type: Number,
        default: 6
    },
    disabled: Boolean,
    isInvalid: Boolean,
    errorMessage: String,
    spaceBetween: Boolean
});
const numberCodeForm = ref(null);
const model = defineModel();
if (!model.value) {
    model.value = Array(props.necessaryLength).fill(null);
}
const handleInput = ({ target }, index) => {
    model.value[index] = target.value;
    if (!target.value.length) {
        target.value = null;
        return;
    }

    const inputLength = target.value.length;

    if (inputLength > 1) {
        const inputValues = target.value.split('');

        inputValues.forEach((value, valueIndex) => {
            const nextValueIndex = index + valueIndex;

            if (nextValueIndex >= props.necessaryLength) return;

            numberCodeForm.value.querySelectorAll('[data-number-code-input]')[
                nextValueIndex
            ].value = value;
            model.value[nextValueIndex] = value;
        });

        index += inputValues.length - 2;
    }
    const nextIndex = index + 1;

    if (nextIndex < props.necessaryLength) {
        numberCodeForm.value
            .querySelectorAll('[data-number-code-input]')
            [nextIndex].focus();
    }
};
const handleKeyDown = (e, index) => {
    const { code, target } = e;
    const previousIndex = index - 1;
    const nextIndex = index + 1;

    const hasPreviousIndex = previousIndex >= 0;
    const hasNextIndex = nextIndex <= props.necessaryLength - 1;

    switch (code) {
        case 'ArrowLeft':
        case 'ArrowUp':
            if (hasPreviousIndex) {
                numberCodeForm.value
                    .querySelectorAll('[data-number-code-input]')
                    [previousIndex].focus();
            }
            e.preventDefault();
            break;

        case 'ArrowRight':
        case 'ArrowDown':
            if (hasNextIndex) {
                numberCodeForm.value
                    .querySelectorAll('[data-number-code-input]')
                    [nextIndex].focus();
            }
            e.preventDefault();
            break;

        case 'Backspace':
            if (!target.value.length && hasPreviousIndex) {
                const prevInput = numberCodeForm.value.querySelectorAll(
                    '[data-number-code-input]'
                )[previousIndex];
                model.value[previousIndex] = null;
                prevInput.value = null;
                prevInput.focus();
            }
            break;

        default:
            break;
    }
};
</script>

<style lang="scss" scoped>
@import '../assets/styles/_main.scss';

.scope-number-input__form {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;

    .scope-number-input__fieldset {
        display: flex;
        padding: 0;
        gap: 12px;
        width: 100%;
        border: 0;

        &.-space-between {
            gap: 0;
            justify-content: space-between;
        }

        .scope-number-input {
            width: 40px;
            height: 40px;

            padding: 10px;
            border-radius: 12px;
            border: 0;
            outline: 1px solid $border;
            -moz-appearance: textfield;

            background: $card-background;
            color: $text-high-emphasis;
            @include fz-16;
            font-weight: 600;
            text-align: center;

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            &:focus-visible:not(:disabled),
            &:active:not(:disabled) {
                outline: 1px solid $primary-soft;
            }

            &.-invalid {
                outline: 1px solid $border-error;
            }
        }
    }

    .scope-number-input__error-message {
        @include fz-14;
        color: $text-error-accent;
    }
}
</style>
